<script>
  import { symbols, selectedSymbol, formatCurrency, formatPercentage } from '../../lib/store.js';
  import { getCryptoIconOrLetters, getCryptoGradient } from '../../lib/cryptoIcons.js';
  
  let searchTerm = '';
  let sortBy = 'volume'; // 'name', 'price', 'change', 'volume'
  let sortAsc = false;
  let activeTab = 'USD'; // 'USD', 'BTC', 'ETH', 'ALL'
  let priceUpdates = {}; // Track price updates for animation
  
  const tabs = [
    { id: 'USD', label: 'USD' },
    { id: 'BTC', label: 'BTC' },
    { id: 'ETH', label: 'ETH' },
    { id: 'ALL', label: 'All' }
  ];
  
  // Track price changes for animation
  let previousPrices = {};
  
  $: {
    $symbols.forEach(symbol => {
      const prevPrice = previousPrices[symbol.symbol];
      const currentPrice = symbol.lastPrice;
      
      if (prevPrice !== undefined && 
          prevPrice !== currentPrice && 
          Math.abs(prevPrice - currentPrice) > 0.01) {
        // Price changed, trigger animation
        priceUpdates = { ...priceUpdates, [symbol.symbol]: true };
        setTimeout(() => {
          priceUpdates = { ...priceUpdates, [symbol.symbol]: false };
        }, 1000);
      }
      previousPrices[symbol.symbol] = currentPrice;
    });
  }
  
  $: filteredSymbols = $symbols
    .filter(s => {
      // Filter by tab based on symbol suffix
      if (activeTab !== 'ALL') {
        const symbolUpper = s.symbol.toUpperCase();
        if (!symbolUpper.endsWith(`/${activeTab}`)) {
          return false;
        }
      }
      
      // Filter by search term
      const search = searchTerm.toLowerCase();
      return s.symbol.toLowerCase().includes(search) ||
             s.baseAssetName?.toLowerCase().includes(search) ||
             s.quoteAssetName?.toLowerCase().includes(search);
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      switch(sortBy) {
        case 'name':
          aVal = a.symbol;
          bVal = b.symbol;
          break;
        case 'price':
          aVal = a.lastPrice || 0;
          bVal = b.lastPrice || 0;
          break;
        case 'change':
          aVal = a.priceChange || 0;
          bVal = b.priceChange || 0;
          break;
        case 'volume':
          aVal = a.baseVol || 0;
          bVal = b.baseVol || 0;
          break;
        default:
          aVal = a.baseVol || 0;
          bVal = b.baseVol || 0;
      }
      
      if (sortAsc) {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  
  // Count symbols per tab based on symbol suffix
  $: symbolCounts = {
    USD: $symbols.filter(s => s.symbol.toUpperCase().endsWith('/USD')).length,
    BTC: $symbols.filter(s => s.symbol.toUpperCase().endsWith('/BTC')).length,
    ETH: $symbols.filter(s => s.symbol.toUpperCase().endsWith('/ETH')).length,
    ALL: $symbols.length
  };
  
  function selectCrypto(symbol) {
    selectedSymbol.set(symbol);
  }
  
  function toggleSort(field) {
    if (sortBy === field) {
      sortAsc = !sortAsc;
    } else {
      sortBy = field;
      sortAsc = false;
    }
  }
  
  function selectTab(tabId) {
    activeTab = tabId;
  }
  
  function getIconInfo(crypto) {
    // Extract base asset from symbol (e.g., BTC from BTC/USD)
    const baseAsset = crypto.symbol.split('/')[0];
    return getCryptoIconOrLetters(baseAsset, crypto.baseAssetName);
  }
  
  function getGradient(crypto) {
    const baseAsset = crypto.symbol.split('/')[0];
    return getCryptoGradient(baseAsset);
  }
</script>

<div class="crypto-list">
  <div class="crypto-list-header">
    <h2>Markets</h2>
    <div class="crypto-search-box">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="7" cy="7" r="5" stroke="currentColor" stroke-width="1.5"/>
        <path d="M11 11l3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
      <input 
        type="text"
        class="crypto-search-input" 
        placeholder="Search crypto..." 
        bind:value={searchTerm}
      />
    </div>
  </div>
  
  <div class="crypto-tabs">
    {#each tabs as tab}
      <button 
        class="crypto-tab {activeTab === tab.id ? 'active' : ''}"
        on:click={() => selectTab(tab.id)}
      >
        {tab.label}
        <span class="crypto-tab-count">{symbolCounts[tab.id] || 0}</span>
      </button>
    {/each}
  </div>
  
  <div class="crypto-sort-controls">
    <button 
      class="crypto-sort-btn {sortBy === 'name' ? 'active' : ''}"
      on:click={() => toggleSort('name')}
    >
      Name
    </button>
    <button 
      class="crypto-sort-btn {sortBy === 'price' ? 'active' : ''}"
      on:click={() => toggleSort('price')}
    >
      Price
    </button>
    <button 
      class="crypto-sort-btn {sortBy === 'change' ? 'active' : ''}"
      on:click={() => toggleSort('change')}
    >
      24h
    </button>
  </div>
  
  <div class="crypto-list-container">
    {#if filteredSymbols.length === 0}
      <div class="crypto-list-empty">
        <p>No markets found</p>
        <span>Try a different search or tab</span>
      </div>
    {:else}
      {#each filteredSymbols as crypto}
        <button 
          class="crypto-item {$selectedSymbol === crypto.symbol ? 'selected' : ''}"
          on:click={() => selectCrypto(crypto.symbol)}
        >
          <div class="crypto-item-info">
            {#if getIconInfo(crypto).type === 'icon'}
              <div class="crypto-item-icon icon-symbol" style="background: {getGradient(crypto)}">
                {getIconInfo(crypto).value}
              </div>
            {:else}
              <div class="crypto-item-icon" style="background: {getGradient(crypto)}">
                {getIconInfo(crypto).value}
              </div>
            {/if}
            <div class="crypto-item-details">
              <div class="crypto-item-symbol">{crypto.symbol}</div>
              <div class="crypto-item-name">{crypto.baseAssetName}</div>
            </div>
          </div>
          <div class="crypto-item-price">
            <div class="crypto-item-price-value {priceUpdates[crypto.symbol] ? 'price-updated' : ''}">
              {formatCurrency(crypto.lastPrice, crypto.quoteDecimalPlaces || 2)}
            </div>
            <div class="crypto-item-price-change {crypto.priceChange >= 0 ? 'positive' : 'negative'}">
              {formatPercentage(crypto.priceChange)}
            </div>
          </div>
        </button>
      {/each}
    {/if}
  </div>
</div>

<style>
  @import './CryptoList.css';
</style>